<template>
    <template v-if="canUseV2LayoutChooser">
        <layout-chooser-v2
            :visible="showLayoutChooser"
            :title="$t('guidesConversationalAiContainer.Add New Step')"
            :app-id="appId"
            :guide="guide"
            @close="closeLayoutChooser"
            @select-layout="handleLayoutSelected" />
    </template>
    <template v-else>
        <layout-chooser
            v-if="showLayoutChooser"
            :title="$t('guidesConversationalAiContainer.Add New Step')"
            :app-id="appId"
            :guide="guide"
            @close="closeLayoutChooser"
            @select-layout="handleLayoutSelected" />
    </template>
    <pendo-page-content class="guide-conversation__content">
        <div class="header">
            <div class="header__modal">
                <div class="header__modal-left">
                    <h2 class="header__modal-title">
                        {{ $t('guidesConversationalAiContainer.Edit guide') }}
                    </h2>
                </div>
                <div class="header__modal-center">
                    <pendo-icon
                        type="guide"
                        size="14" />
                    <span class="header__modal-guide-name">{{ generatedGuideName }}</span>
                </div>
                <div class="header__modal-right">
                    <pendo-button
                        theme="app"
                        type="subtle"
                        icon="save"
                        :label="$t('guidesConversationalAiContainer.Save guide')"
                        :disabled="saveDisabled"
                        @click="$emit('create-guide')" />
                    <pendo-icon-button
                        icon="x"
                        tooltip="Close"
                        data-cy="back-button"
                        aria-label="close"
                        @click="toggleBackModal" />
                </div>
            </div>
            <pendo-divider v-bind="{ strokeWidth: 2, height: '1px' }" />
        </div>
        <div
            :class="`guide-conversation__wrapper ${isPreviewHidden ? 'no-preview' : ''}`">
            <div class="guide-conversation__spacer" />
            <div
                v-if="showChat"
                class="guide-conversation__thread">
                <ask-pendo-thread
                    :input-placeholder="inputPlaceholder"
                    :messages="messages"
                    :conversation-id="id"
                    :agent-id="agentId"
                    :pending="isLoading"
                    :error="errorMessage"
                    :custom-button-icon="`arrow-right`"
                    :is-guide-chat="isGuideChat"
                    :should-show-llm-undo="shouldShowLlmUndo"
                    @update="handleUpdate"
                    @stop="$emit('stop')"
                    @undo-llm-change="$emit('undo-llm-change')">
                    <template
                        #loading
                        v-if="isLoading">
                        <ai-chat-loading is-guide-chat />
                    </template>
                </ask-pendo-thread>
            </div>
            <transition name="slide-preview">
                <pendo-card
                    v-if="!isPreviewHidden"
                    class="guide-conversation__preview"
                    :is-loading="isLoading"
                    :style="{ '--loading-overlay-z-index': LOADING_OVERLAY_Z_INDEX }">
                    <template #title>
                        <div class="guide-conversation__title-wrapper">
                            <span
                                v-if="segment"
                                class="guide-conversation__segment">
                                <pendo-router-link
                                    :to="{ name: 'Segments', query: { segment: segment.id } }"
                                    target="_blank">
                                    <pendo-button
                                        theme="app"
                                        type="subtle"
                                        class="ask-pendo-next-actions__button"
                                        size="small">
                                        {{ 'Segment: ' + segment.name }}
                                    </pendo-button>
                                </pendo-router-link>
                            </span>
                            <div
                                v-pendo-tooltip="!allStepsHaveSameTheme && themePickerTooltip"
                                class="pendo-layout-chooser__theme-selector">
                                <pendo-multiselect
                                    :value="themeOrPlaceholder"
                                    label-key="name"
                                    value-key="id"
                                    preselect-first
                                    :loading="loading"
                                    :allow-empty="false"
                                    :disabled="!allStepsHaveSameTheme"
                                    :labels="{ left: themeLabel }"
                                    :options="themes"
                                    @select="selectTheme">
                                    <template #aside="{ highlightedOption }">
                                        <theme-preview
                                            v-if="(highlightedOption || theme) && (highlightedOption || theme).buildingBlocks"
                                            :theme="highlightedOption || theme" />
                                    </template>
                                </pendo-multiselect>
                            </div>
                        </div>
                    </template>
                    <guides-conversational-preview-empty-state
                        v-if="stepsWithDom.length === 0"
                        @primary-action="openLayoutChooser(0)" />
                    <guides-editable-step-preview
                        v-for="(step, index) in stepsWithDom"
                        :key="`preview-selected-${step.key}`"
                        :step-index="index"
                        :dom="step.dom"
                        :step="step"
                        :is-mobile="isMobile"
                        :guides="guides"
                        @delete-step="handleDeleteStep(index)"
                        @copy-step="$emit('copy-step', index)"
                        @open-ai-modal="handleOpenAiModal"
                        @close-ai-modal="handleCloseAiModal"
                        @open-layout-chooser="openLayoutChooser(index + 1)"
                        @content-block-update="$emit('content-block-update', $event)"
                        @toggle-undo-button="handleToggleUndoButton" />
                </pendo-card>
            </transition>
            <guides-conversational-ai-modal
                v-if="aiModal.visible"
                :visible="aiModal.visible"
                :target-element="aiModal.targetElement"
                :component="aiModal.component"
                @update-component="handleUpdateComponent"
                @close="handleCloseAiModal"
                @request-content-change="handleRequestContentChange">
                <template #toolbarItems>
                    <pendo-icon-button
                        v-pendo-tooltip="undoButtonTooltip"
                        class="ai-toolbar-floating__undo-btn"
                        icon="rotate-counter-clockwise"
                        icon-size="16"
                        :disabled="!aiModal.isUndoEnabled && !aiResponsesStack.length"
                        @click="handleUndo" />
                    <pendo-icon-button
                        v-if="!aiModal.lastPrompt && showChat"
                        v-pendo-tooltip="editButtonTooltip"
                        class="ai-toolbar-floating__wand-btn"
                        icon="wand"
                        icon-size="16"
                        @click="handleUpdateComponent('modal')" />
                    <pendo-icon-button
                        v-if="aiModal.lastPrompt && showChat"
                        v-pendo-tooltip="redoButtonTooltip"
                        class="ai-toolbar-floating__wand-btn"
                        icon="refresh-cw"
                        icon-size="16"
                        @click="handleRedoAiEdit" />
                </template>
            </guides-conversational-ai-modal>
        </div>
    </pendo-page-content>
    <pendo-modal
        :visible="showBackModal"
        :title="$t('guidesConversationalAiContainer.You have unsaved changes')"
        :message="handleBackModalMessage()"
        height="auto"
        :show-close="false"
        :confirm-button-config="{
            type: 'primary',
            label: goBackLabel,
        }"
        :cancel-button-config="{
            type: 'secondary',
            label: cancelLabel,
        }"
        @cancel="toggleBackModal"
        @confirm="$emit('exit')" />
</template>

<script>
import {
    PendoButton,
    PendoDivider,
    PendoIconButton,
    PendoModal,
    PendoPageContent,
    PendoIcon,
    PendoCard,
    PendoMultiselect,
    PendoTooltip,
} from '@pendo/components';
import LayoutChooser from '@/common/layout-chooser/layout-chooser.vue';
import LayoutChooserV2 from '@/common/layout-chooser-v2/layout-chooser-v2.vue';
import AskPendoThread from '@/ask-pendo/components/ask-pendo-thread.vue';
import GuidesEditableStepPreview from './guides-editable-step-preview.vue';
import GuidesConversationalAiModal from './guides-conversational-ai-modal.vue';
import AiChatLoading from '@/voc/components/ai-chat-loading/ai-chat-loading.vue';
import { EDITABLE_CONTENT_TYPE, PENDO_EDITING_ID_ATTRIBUTE, getElementByEditingId, DEFAULT_STEP_Z_INDEX } from './editable-elements.utils';
import { getBlockContent } from '@/guides/helpers/templates/utils/ai-editor-utils';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import GuidesConversationalPreviewEmptyState from './guides-conversational-preview-empty-state.vue';
import ThemePreview from '@/surveys/pages/create-survey-wizard/configure-survey/theme-preview/theme-preview.vue';
import { mapGetters } from 'vuex';

const LOADING_OVERLAY_Z_INDEX = DEFAULT_STEP_Z_INDEX + 1;

export default {
    name: 'GuidesConversationalAiContainer',
    components: {
        AiChatLoading,
        PendoButton,
        PendoCard,
        PendoDivider,
        PendoIconButton,
        PendoIcon,
        AskPendoThread,
        PendoModal,
        PendoPageContent,
        PendoMultiselect,
        LayoutChooser,
        LayoutChooserV2,
        PendoRouterLink,
        GuidesConversationalPreviewEmptyState,
        GuidesEditableStepPreview,
        GuidesConversationalAiModal,
        ThemePreview,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        shouldShowLlmUndo: {
            type: Boolean,
            default: false,
        },
        appId: {
            type: Number,
            required: true,
        },
        isPreviewHidden: {
            type: Boolean,
            default: false,
        },
        themes: {
            type: Array,
            default: () => [],
        },
        theme: {
            type: Object,
            required: true,
        },
        id: {
            type: String,
            required: true,
        },
        conversation: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        generatedGuideName: {
            type: String,
            default: '',
        },
        steps: {
            type: Array,
            default: () => [],
        },
        errorMessage: {
            type: String,
            default: '',
        },
        saveDisabled: {
            type: Boolean,
            default: false,
        },
        guideId: {
            type: String,
            default: '',
        },
        guide: {
            type: Object,
            default: null,
        },
        guides: {
            type: Array,
            default: () => [],
        },
        turndownService: {
            type: Object,
            required: true,
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
        segment: {
            type: Object,
            default: () => null,
        },
        allStepsHaveSameTheme: {
            type: Boolean,
            default: true,
        },
        agentId: {
            type: String,
            default: null,
        },
        isFromAgentMode: {
            type: Boolean,
            default: false,
        },
        showChat: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'content-block-update',
        'create-guide',
        'delete-step',
        'exit',
        'on-theme-change',
        'preview-ready',
        'send-message',
        'stop',
        'undo-llm-change',
        'copy-step',
        'add-step',
    ],
    data () {
        return {
            showLayoutChooser: false,
            showBackModal: false,
            isGuideChat: true,
            loading: false,
            aiResponsesStack: [],
            aiModal: {
                visible: false,
                targetElement: null,
                lastPrompt: null,
                targetElementOriginalText: null,
            },
            LOADING_OVERLAY_Z_INDEX,
        };
    },
    computed: {
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
        }),
        canUseV2LayoutChooser () {
            return this.hasSegmentFlag('guideLayoutChooserV2');
        },
        messages () {
            return this.conversation ? this.conversation.messages : [];
        },
        stepsWithDom () {
            return this.steps.filter((step) => step.dom);
        },
        themeOrPlaceholder () {
            return this.theme || { id: null, name: this.$t('guidesConversationalAiContainer.Select a theme') };
        },
    },
    watch: {
        async steps () {
            if (this.aiModal.lastPrompt) {
                const { targetElement } = this.aiModal;
                const editingId = targetElement.getAttribute(PENDO_EDITING_ID_ATTRIBUTE);
                await this.$nextTick();
                await this.$nextTick(); // this second $nextTick() is a short-term fix, we'll ship a better fix soon(ish)
                const rerenderedTargetElement = getElementByEditingId(editingId);

                this.handleOpenAiModal(
                    { ...this.aiModal, targetElement: rerenderedTargetElement },
                    { forceReopen: true },
                );
            }
        },
    },
    created () {
        this.inputPlaceholder = this.$t('guidesConversationalAiContainer.Write your answer');
        this.goBackLabel = this.$t('guidesConversationalAiContainer.Go back');
        this.cancelLabel = this.$t('Cancel');
        this.themeLabel = this.$t('guidesConversationalAiContainer.Theme');
        this.themePickerTooltip = this.$t('guidesConversationalAiContainer.themePickerTooltip');
        this.editButtonTooltip = this.$t('guidesConversationalAiContainer.editButtonTooltip');
        this.undoButtonTooltip = this.$t('guidesConversationalAiContainer.undoButtonTooltip');
        this.redoButtonTooltip = this.$t('guidesConversationalAiContainer.redoButtonTooltip');
    },
    methods: {
        closeLayoutChooser () {
            this.showLayoutChooser = false;
        },
        handleLayoutSelected ({ steps }) {
            this.$emit('add-step', { steps, index: this.stepIndex });

            this.closeLayoutChooser();
        },
        openLayoutChooser (stepIndex) {
            this.stepIndex = stepIndex;
            this.showLayoutChooser = true;
        },
        toggleBackModal () {
            this.showBackModal = !this.showBackModal;
        },
        buildPrompt ({ text, action }) {
            const { stepIndex } = this.aiModal;
            const stepNumber = stepIndex + 1;

            const prompts = {
                makeShorter: this.$t('guidesConversationalAiContainer.shortenPrompt', { text, stepNumber }),
                makeLonger: this.$t('guidesConversationalAiContainer.lengthenPrompt', { text, stepNumber }),
                makeProfessional: this.$t('guidesConversationalAiContainer.rewriteProfessionalPrompt', {
                    text,
                    stepNumber,
                }),
                makeCasual: this.$t('guidesConversationalAiContainer.rewriteCasualPrompt', { text, stepNumber }),
                makeConversational: this.$t('guidesConversationalAiContainer.rewriteConversationalPrompt', {
                    text,
                    stepNumber,
                }),
                makeDirect: this.$t('guidesConversationalAiContainer.rewriteDirectPrompt', { text, stepNumber }),
                makeExpert: this.$t('guidesConversationalAiContainer.rewriteTechnicalPrompt', { text, stepNumber }),
                makeFriendly: this.$t('guidesConversationalAiContainer.rewriteFriendlyPrompt', { text, stepNumber }),
            };

            return prompts[action];
        },
        pushCurrentAIContent () {
            const type = this.aiModal.targetElement.getAttribute(EDITABLE_CONTENT_TYPE);
            const blockContent = getBlockContent({
                type,
                event: { data: { dom: this.aiModal.targetElement } },
                turndownService: this.turndownService,
            });

            this.aiResponsesStack.push(blockContent);
        },
        handleRequestContentChange ({ text, action }) {
            const optionSelected = { text, action };
            const prompt = this.buildPrompt(optionSelected);
            this.aiModal.lastPrompt = prompt;
            this.handleUpdate({ text: prompt });
            this.pushCurrentAIContent();
            this.handleUpdateComponent('toolbar');
            this.hideAiModal();
        },
        async handleUpdate (message, artifact = null) {
            const { text } = message;
            this.$emit(
                'send-message',
                {
                    conversationId: this.id,
                    type: 'user',
                    content: text,
                },
                artifact,
            );
        },
        selectTheme (theme) {
            this.$emit('on-theme-change', theme);
        },
        handleUpdateComponent (component) {
            this.aiModal.component = component;
        },
        handleOpenAiModal (
            { targetElement, targetElementOriginalText, lastPrompt, stepIndex, targetElementBuildingBlock },
            { forceReopen = false } = {},
        ) {
            if (!forceReopen) {
                this.aiResponsesStack = [];

                if (targetElement === this.aiModal.targetElement) {
                    this.aiModal.lastPrompt = '';
                    this.aiModal.component = 'toolbar';
                    this.aiModal.targetElementOriginalText = targetElementOriginalText;

                    return;
                }
            }

            this.aiModal = {
                visible: true,
                component: 'toolbar',
                isUndoEnabled: false,
                targetElement,
                lastPrompt,
                stepIndex,
                targetElementOriginalText,
                targetElementBuildingBlock,
            };
        },
        hideAiModal () {
            this.aiModal.visible = false;
        },
        handleCloseAiModal () {
            this.aiModal = {
                visible: false,
                targetElement: null,
                isUndoEnabled: false,
                lastPrompt: null,
                stepIndex: null,
                targetElementBuildingBlock: null,
            };
            this.aiResponsesStack = [];
        },
        handleToggleUndoButton () {
            if (!this.aiModal.targetElement) return;

            const hasChanges = this.aiModal.targetElementOriginalText !== this.aiModal.targetElement.textContent;
            this.aiModal.isUndoEnabled = hasChanges;
        },
        handleRedoAiEdit () {
            this.pushCurrentAIContent();

            const revertedArtifact = { ...this.conversation.artifact };

            this.handleUpdate({ text: this.aiModal.lastPrompt }, revertedArtifact);
            this.hideAiModal();
        },
        handleUndo () {
            if (this.aiModal.lastPrompt && this.aiResponsesStack?.length > 0) {
                this.handleUndoAiEdit();
            } else {
                this.handleUndoInlineEdit();
            }
        },
        handleUndoAiEdit () {
            const lastValue = this.aiResponsesStack.pop();
            const type = this.aiModal.targetElement.getAttribute(EDITABLE_CONTENT_TYPE);
            const editingId = this.aiModal.targetElement.getAttribute(PENDO_EDITING_ID_ATTRIBUTE);

            this.$emit('content-block-update', {
                data: { dom: this.aiModal.targetElement },
                editingId,
                stepIndex: this.aiModal.stepIndex,
                type,
                needsRefresh: true,
                content: lastValue,
            });
        },
        handleUndoInlineEdit () {
            const editableContent =
                this.aiModal.targetElement.querySelector('.pendo-guide-editable-content-area') ||
                this.aiModal.targetElement;
            editableContent.textContent = this.aiModal.targetElementOriginalText;
            this.handleOpenAiModal({
                targetElement: this.aiModal.targetElement,
                stepIndex: this.aiModal.stepIndex,
                targetElementOriginalText: this.aiModal.targetElementOriginalText,
            });
            this.aiModal.isUndoEnabled = false;
        },
        handleBackModalMessage () {

            if (this.isFromAgentMode) {
                return this.$t('guidesConversationalAiContainer.backModalForAgentMode');
            }

            if (this.guideId) {
                return this.$t('guidesConversationalAiContainer.backModalForGuideDetails');
            }

            return this.$t('guidesConversationalAiContainer.backModalForLayouts');
        },
        handleDeleteStep (index) {
            this.aiModal.lastPrompt = '';
            this.handleCloseAiModal();
            this.$emit('delete-step', index);
        },
    },
};
</script>

<style lang="less" scoped>
.header {
    &__actions {
        display: flex;
        justify-content: space-between;
        padding: 16px;
    }

    &__modal {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 16px;
        background-color: var(--palette-gray-0);

        &-title {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: var(--palette-gray-100);
        }

        &-center {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: center;
            padding: 0 24px;
        }

        &-guide-name {
            font-family: Inter;
            font-weight: 600;
            font-style: 'semi-bold';
            font-size: 14.22px;
            line-height: 150%;
            letter-spacing: 0%;
            vertical-align: middle;
        }

        &-right {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            gap: 16px;
        }
    }
}

:deep(.pendo-modal) {
    &__header {
        border-bottom: none;
    }

    &__title {
        padding: 16px;
    }

    &__body {
        padding: 0px 16px 24px 16px;
    }

    &__footer {
        border-top: none;
    }
}

:deep(.pendo-card) {
    border-right: none;
}

:deep(.pendo-card__body) {
    background-color: var(--palette-gray-20);
    min-height: 100% !important;
}

:deep(.pendo-card__header--left) {
    width: 100%;
}

.guide-conversation {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &__content {
        display: flex;
        border-top: none;
        flex-direction: column;
        flex-wrap: nowrap;
        overflow: auto;
        min-height: 0;
        padding: 0;
        height: 100%;

        :deep(.conversation-input) {
            min-width: 0;
        }
    }

    &__wrapper {
        display: flex;
        flex-grow: 1;
        height: 0;

        &.no-preview {
            gap: 0;
            .guide-conversation__spacer {
                width: calc(50% - 244px);
            }
        }
    }

    &__spacer {
        width: 0;
        flex-shrink: 0;
        transition: width 0.3s ease-in;
    }

    &__thread {
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        width: 512px;
        height: 100%;
        position: relative;
        z-index: 2;
        background-color: var(--palette-gray-0);
        transition: transform 0.3s ease-in;

        > * {
            flex: 1;
            min-height: 0;
        }

        :deep(.ask-pendo-thread) {
            padding: 0;
        }

        :deep(.ask-pendo-message:first-child) {
            h2 {
                margin-top: 2px;
            }
        }
        :deep(.ask-pendo-thread__messages-container) {
            padding: 24px 16px 0;
        }

        :deep(.ask-pendo-thread__input) {
            padding-bottom: 0;
        }

        :deep(.ask-pendo-thread__feedback-row) {
            margin-left: 24px;
        }
    }

    &__preview {
        display: flex;
        flex-direction: column;
        height: 100%;
        flex-grow: 4;
        position: relative;
        z-index: 1;
        border-radius: 0px;
        border-top: none;
        border-bottom: none;

        :deep(.pendo-card__loading-overlay) {
            background-color: var(--palette-gray-20);
            opacity: 0.75;
            transition: opacity 0.3s ease-in;
            z-index: var(--loading-overlay-z-index);
        }

        :deep(.pendo-card__loading-indicator) {
            z-index: var(--loading-overlay-z-index);
        }

        :deep(.pendo-card-loading-overlay-enter-from),
        :deep(.pendo-card-loading-overlay-leave-to) {
            opacity: 0;
        }

        :deep(.pendo-card__content) {
            min-height: 0;
            flex-grow: 1;
            display: block;
            overflow: auto;
        }

        :deep(.step-preview) {
            padding-bottom: 60px;
            height: auto;
            min-height: 0;
            max-height: unset;
        }

        :deep(.step-preview > .preview) {
            position: relative;
            left: 0;
            transform: none !important;
            margin: 0 auto;
        }
    }
}

.guide-conversation__title-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 32px;
}

.guide-conversation__title-text {
    flex-grow: 2;
}

.slide-preview-enter-active,
.slide-preview-leave-active {
    transition: transform 0.3s ease-in, opacity 1s ease-in;
}

.slide-preview-enter-from,
.slide-preview-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.slide-preview-enter-to,
.slide-preview-leave-from {
    opacity: 1;
    transform: translateX(0);
}

:deep(.pendo-multiselect--inline-label) {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.ai-toolbar-floating {
    &__undo-btn {
        border-radius: 3px 0 0 3px;
        border-right: 1px solid var(--palette-gray-40);
        color: var(--palette-gray-100);
        width: 28px;
        margin: 0px;
    }

    &__wand-btn {
        border-radius: 0 3px 3px 0;
        border-left: 1px solid var(--palette-gray-40);
        color: var(--palette-gray-100);
        width: 28px;
        margin: 0px;
    }
}
</style>
